/*
 * Copyright © 2005 - 2021 Alfresco Software, Ltd. All rights reserved.
 *
 * License rights for this program may be obtained from Alfresco Software, Ltd.
 * pursuant to a written agreement and any use of this program without such an
 * agreement is prohibited.
 */
import { AppStore, SnackbarErrorAction, SnackbarInfoAction } from '@alfresco/aca-shared/store';
import { AlfrescoApiService, TranslationService } from '@alfresco/adf-core';
import { PeopleApi, Person } from '@alfresco/js-api';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { throwError } from 'rxjs';

@Component({
  selector: 'app-view-profile',
  templateUrl: './view-profile.component.html',
  styleUrls: ['./view-profile.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ViewProfileComponent implements OnInit {
  peopleApi: PeopleApi;

  profileForm: FormGroup;
  passwordForm: FormGroup;
  personDetails: Person;

  generalSectionDropdown = true;
  generalSectionButtonsToggle = true;

  loginSectionDropdown = true;
  loginSectionButtonsToggle = true;
  passwordSectionDropdown = false;

  contactSectionDropdown = false;
  contactSectionButtonsToggle = true;

  //Expresión regular para que la contraseña debe tener al menos 8 caracteres, incluir una letra, un número y un símbolo
  passwordPattern = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&.,;:-_=])[A-Za-z\d@$!%*?&.,;:-_=]{8,}$/;

  constructor(
    private formBuilder: FormBuilder, 
    private router: Router, 
    apiService: AlfrescoApiService, 
    private store: Store<AppStore>, 
    private translationService: TranslationService) {
      
    this.peopleApi = new PeopleApi(apiService.getInstance());
  }

  ngOnInit() {
    this.populateForm(this.personDetails);

    this.peopleApi
      .getPerson('-me-')
      .then((userInfo) => {
        this.personDetails = userInfo?.entry;
        this.populateForm(userInfo?.entry);
      })
      .catch((error) => {
        throwError(error);
      });
  }

  populateForm(userInfo: Person) {
    this.profileForm = this.formBuilder.group({
      jobTitle: [userInfo?.jobTitle || ''],
      location: [userInfo?.location || ''],
      telephone: [userInfo?.telephone || '', Validators.pattern('^[0-9]*$')],
      mobile: [userInfo?.mobile || '', Validators.pattern('^[0-9]*$')],
      companyPostCode: [userInfo?.company?.postcode || ''],
      companyAddress: [userInfo?.company?.address1 || ''],
      companyTelephone: [userInfo?.company?.telephone || '', Validators.pattern('^[0-9]*$')],
      companyEmail: [userInfo?.company?.email || '', Validators.email]
    });

    this.passwordForm = this.formBuilder.group({
      oldPassword: new FormControl('', Validators.required),
      newPassword: new FormControl('', [Validators.required, Validators.pattern(this.passwordPattern)]),
      verifyPassword: new FormControl('', [Validators.required, Validators.pattern(this.passwordPattern)])
    });
  }

  navigateToPersonalFiles() {
    this.router.navigate(['/personal-files'], {
      replaceUrl: true
    });
  }

  toggleGeneralDropdown() {
    this.generalSectionDropdown = !this.generalSectionDropdown;

    if (!this.generalSectionDropdown) {
      this.generalSectionButtonsToggle = true;
    }
  }

  toggleGeneralButtons() {
    this.generalSectionButtonsToggle = !this.generalSectionButtonsToggle;

    if (!this.generalSectionButtonsToggle) {
      this.generalSectionDropdown = true;
    }
  }

  onSaveGeneralData(event) {
    this.generalSectionButtonsToggle = !this.generalSectionButtonsToggle;
    this.updatePersonDetails(event);
  }

  onSaveLoginData(event) {
    if (this.passwordForm.valid) {
      //Controlar si las contraseña nueva y la de verificación coinciden
      if(this.verifyNewPassword()){
        this.resetCustomError('oldPassword');
        //Actualizar contraseña
        this.peopleApi
        .updatePerson(this.personDetails.id, {
          oldPassword: event.value.oldPassword,
          password: event.value.newPassword
        })
        .then((person) => {
          this.store.dispatch(new SnackbarInfoAction('ALDEN.MESSAGES.SUCCESS.PROFILE_PASSWORD'));
          this.personDetails = person?.entry;
          this.populateForm(person?.entry);
          this.passwordSectionDropdown = !this.passwordSectionDropdown;
          this.loginSectionButtonsToggle = !this.loginSectionButtonsToggle;
        })
        .catch((error) => {
          //Parseamos error devuelto
          let errorJSON = JSON.parse(error.message);
          switch(errorJSON.error.statusCode){
            case 403: //Contraseña actual incorrecta
              this.store.dispatch(new SnackbarErrorAction('ALDEN.MESSAGES.ERRORS.OLD_PASSWORD'));
              this.setFieldInvalid('oldPassword', { customError: 'ALDEN.MESSAGES.ERRORS.OLD_PASSWORD' });
              break;
            case 500: //Internal Server Error
              this.store.dispatch(new SnackbarErrorAction('ALDEN.MESSAGES.ERRORS.RESPONSE.500'));
              break;
          }
          throwError(error);
        });
      }
    } else {
      this.store.dispatch(new SnackbarErrorAction('ALDEN.MESSAGES.ERRORS.REQUIRED_FIELDS'));
    }
  }

  onSaveCompanyData(event) {
    this.contactSectionButtonsToggle = !this.contactSectionButtonsToggle;
    this.updatePersonDetails(event);
  }

  toggleLoginDropdown() {
    this.loginSectionDropdown = !this.loginSectionDropdown;

    if (!this.loginSectionDropdown) {
      this.loginSectionButtonsToggle = true;
    }
  }

  toggleLoginButtons() {
    this.loginSectionButtonsToggle = !this.loginSectionButtonsToggle;
    this.passwordSectionDropdown = !this.passwordSectionDropdown;

    if (!this.loginSectionButtonsToggle) {
      this.loginSectionDropdown = true;
      this.passwordSectionDropdown = true;
    }
  }

  toggleContactDropdown() {
    this.contactSectionDropdown = !this.contactSectionDropdown;

    if (!this.contactSectionDropdown) {
      this.contactSectionButtonsToggle = true;
    }
  }

  toggleContactButtons() {
    this.contactSectionButtonsToggle = !this.contactSectionButtonsToggle;

    if (!this.contactSectionButtonsToggle) {
      this.contactSectionDropdown = true;
    }
  }

  updatePersonDetails(event) {
    if (this.profileForm.valid) {
      this.peopleApi
        .updatePerson(this.personDetails.id, {
          jobTitle: event.value.jobTitle,
          location: event.value.location,
          telephone: event.value.telephone,
          mobile: event.value.mobile,
          company: {
            postcode: event.value.companyPostCode,
            address1: event.value.companyAddress,
            telephone: event.value.companyTelephone,
            email: event.value.companyEmail
          }
        })
        .then((person) => {
          this.personDetails = person?.entry;
          this.populateForm(person?.entry);
        })
        .catch((error) => {
          this.populateForm(this.personDetails);
          throwError(error);
        });
    } else {
      this.populateForm(this.personDetails);
    }
  }

  /**
   * Verifica que newPassword y verifyPassword sean iguales
   * @returns boolean si son iguales o no
   */
  verifyNewPassword(): boolean{
    this.resetCustomError('newPassword');
    this.resetCustomError('verifyPassword');
    //Controlar si las contraseña nueva y la de verificación coinciden
    if(this.passwordForm.get("newPassword").value && this.passwordForm.get("verifyPassword").value 
          && this.passwordForm.get("newPassword").value != this.passwordForm.get("verifyPassword").value){
      this.store.dispatch(new SnackbarErrorAction('ALDEN.MESSAGES.ERRORS.MATCH_PASSWORDS'));
      //Personalización mensaje de error
      this.setFieldInvalid('newPassword', { customError: 'ALDEN.MESSAGES.ERRORS.MATCH_PASSWORDS' });
      this.setFieldInvalid('verifyPassword', { customError: 'ALDEN.MESSAGES.ERRORS.MATCH_PASSWORDS' });
      return false;
    }
    return true
  }

  /**
   * Control del icono a mostrar y tipo de input
   * @param element elemento HTML del icono
   * @param formControlName el nombre del campo del formulario del input a cambiar
   */
  getControlVisibility(element: any, formControlName: string) {
    const formField = document.querySelector(`[formControlName="${formControlName}"]`) as HTMLInputElement;
    if(element.target.innerText.includes("off")){
      formField.type = "text";
      element.target.innerText = "visibility";
    }else{
      formField.type = "password";
      element.target.innerText = "visibility_off";
    }   
  }

  /**
   * Setear mensaje de error
   * @param fieldName campo de formulario
   * @param error error y mensaje
   */
  setFieldInvalid(fieldName: string, error: any) {
    const control = this.passwordForm.get(fieldName);
    if (control) {
      control.setErrors({ ...control.errors, ...error });
      control.markAsTouched();
    }
  }

  /**
   * Obtener el mensaje de error a mostrar
   * @param fieldName campo del formulario
   * @returns error a mostrar
   */
  getErrorMessage(fieldName: string): string {
    const control = this.passwordForm.get(fieldName);
    if (control?.hasError('required')) {
      return this.translationService.instant('APP.SETTINGS.REQUIRED-FIELD');
    }
    if (control?.hasError('pattern')) {
      return this.translationService.instant('ALDEN.MESSAGES.ERRORS.STRONG_PASSWORD');
    }
    if (control?.hasError('customError')) {
      return this.translationService.instant(control.getError('customError'));
    }
    return '';
  }

  /**
   * Resetea el customError de un campo
   * @param fieldName campo de formulario
   */
  resetCustomError(fieldName: string){
    const control = this.passwordForm.get(fieldName);
    if (control.hasError('customError')) {
      const errors = { ...control.errors };
      delete errors['customError'];
      control.setErrors(Object.keys(errors).length ? errors : null);
    }
  }
}
